import { apiClient } from './client';
import {
  USAJILIMeResponse,
  USAJILIMetadata,
  USAJILIFilterState,
  USAJILISummaryRow,
  USAJILITrendPoint,
  USAJILIToDatePoint,
  USAJILIPvtToDate,
  USAJILIPvtTrend,
  USAJILIDailyRecord,
  USAJILIAuditLogRow,
  USAJILILocationRef,
  USAJILIUploadPreviewResponse,
  USAJILIUploadApplyResponse,
  PaginatedResponse,
} from '@types';

/* ================================================================
   Helpers
================================================================= */
function filterToParams(f: Partial<USAJILIFilterState>): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.search) p.search = f.search;
  if (f.date_from) p.date_from = f.date_from;
  if (f.date_to) p.date_to = f.date_to;
  if (f.county && f.county !== 'All') p.county = f.county;
  if (f.district && f.district !== 'All') p.district = f.district;
  if (f.division && f.division !== 'All') p.division = f.division;
  if (f.location && f.location !== 'All') p.location = f.location;
  return p;
}

function buildQs(params: Record<string, string | number>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
}

const BASE = 'usajili';

/* ================================================================
   Auth / role
================================================================= */
export const usajiliApi = {
  me(): Promise<USAJILIMeResponse> {
    return apiClient.get<USAJILIMeResponse>(`${BASE}/me/`);
  },

  metadata(): Promise<USAJILIMetadata> {
    return apiClient.get<USAJILIMetadata>(`${BASE}/metadata/`);
  },

  /* ================================================================
     Views
  ================================================================= */
  summary(filters: Partial<USAJILIFilterState>): Promise<{ rows: USAJILISummaryRow[] }> {
    return apiClient.get(`${BASE}/summary/${buildQs(filterToParams(filters))}`);
  },

  trendVoters(filters: Partial<USAJILIFilterState>): Promise<{ points: USAJILITrendPoint[] }> {
    return apiClient.get(`${BASE}/trend-voters/${buildQs(filterToParams(filters))}`);
  },

  toDate(filters: Partial<USAJILIFilterState>): Promise<{ rows: USAJILIToDatePoint[] }> {
    return apiClient.get(`${BASE}/to-date/${buildQs(filterToParams(filters))}`);
  },

  pvtToDate(filters: Partial<USAJILIFilterState>): Promise<USAJILIPvtToDate> {
    return apiClient.get(`${BASE}/pvt-todate/${buildQs(filterToParams(filters))}`);
  },

  pvtTrend(filters: Partial<USAJILIFilterState>): Promise<USAJILIPvtTrend> {
    return apiClient.get(`${BASE}/pvt-trend/${buildQs(filterToParams(filters))}`);
  },

  /* ================================================================
     Daily data CRUD
  ================================================================= */
  dailyList(params: Partial<USAJILIFilterState> & { page?: number; page_size?: number; ordering?: string }) {
    const p: Record<string, string | number> = filterToParams(params);
    if (params.page) p.page = params.page;
    if (params.page_size) p.page_size = params.page_size;
    if (params.ordering) p.ordering = params.ordering;
    return apiClient.get<PaginatedResponse<USAJILIDailyRecord>>(`${BASE}/daily/${buildQs(p)}`);
  },

  dailyCreate(data: {
    date: string;
    county: string;
    new_ids?: string;
    id_replacements?: string;
    voters_registered?: string;
    district?: string;
    division?: string;
    location?: string;
  }): Promise<USAJILIDailyRecord> {
    return apiClient.post(`${BASE}/daily/`, data);
  },

  dailyUpdate(id: number, data: Record<string, unknown>): Promise<USAJILIDailyRecord> {
    return apiClient.patch(`${BASE}/daily/${id}/`, data);
  },

  dailyDelete(id: number): Promise<{ ok: boolean; id: number }> {
    return apiClient.delete(`${BASE}/daily/${id}/`);
  },

  /* ================================================================
     Upload
  ================================================================= */
  uploadPreview(file: File): Promise<USAJILIUploadPreviewResponse> {
    const fd = new FormData();
    fd.append('file', file);
    // No explicit Content-Type — axios injects multipart/form-data; boundary=... automatically
    return apiClient.post(`${BASE}/upload/preview/`, fd);
  },

  uploadApply(file: File, mode: 'upsert' | 'replace'): Promise<USAJILIUploadApplyResponse> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    return apiClient.post(`${BASE}/upload/apply/`, fd);
  },

  /* ================================================================
     Export
  ================================================================= */
  exportDailyCSV(filters: Partial<USAJILIFilterState>): string {
    // returns the URL so the FE can use window.open / <a download>
    const qs = buildQs(filterToParams(filters));
    const token = (localStorage.getItem('sdics_access_token') || '').replace(/"/g, '');
    return `/api/${BASE}/export/daily.csv${qs}${qs ? '&' : '?'}token=${encodeURIComponent(token)}`;
  },

  exportSummaryCSV(filters: Partial<USAJILIFilterState>): string {
    const qs = buildQs(filterToParams(filters));
    const token = (localStorage.getItem('sdics_access_token') || '').replace(/"/g, '');
    return `/api/${BASE}/export/summary.csv${qs}${qs ? '&' : '?'}token=${encodeURIComponent(token)}`;
  },

  /* ================================================================
     Audit log (ADMIN only)
  ================================================================= */
  auditLog(params: { page?: number; page_size?: number }) {
    const p: Record<string, string | number> = {};
    if (params.page) p.page = params.page;
    if (params.page_size) p.page_size = params.page_size;
    return apiClient.get<PaginatedResponse<USAJILIAuditLogRow>>(`${BASE}/audit-log/${buildQs(p)}`);
  },

  /* ================================================================
     Location reference
  ================================================================= */
  locationRefList(params: { county?: string; page?: number; page_size?: number }) {
    const p: Record<string, string | number> = {};
    if (params.county && params.county !== 'All') p.county = params.county;
    if (params.page) p.page = params.page;
    if (params.page_size) p.page_size = params.page_size;
    return apiClient.get<PaginatedResponse<USAJILILocationRef>>(`${BASE}/location-ref/${buildQs(p)}`);
  },

  locationRefCreate(data: Partial<USAJILILocationRef>): Promise<{ ok: boolean; id: number }> {
    return apiClient.post(`${BASE}/location-ref/`, data);
  },

  locationRefUpdate(id: number, data: Partial<USAJILILocationRef>): Promise<{ ok: boolean }> {
    return apiClient.patch(`${BASE}/location-ref/${id}/`, data);
  },

  locationRefDelete(id: number): Promise<{ ok: boolean }> {
    return apiClient.delete(`${BASE}/location-ref/${id}/`);
  },
};
